import { useState } from 'react';
import { Banknote, Scale, TrendingUp } from 'lucide-react';
import { useFormat } from '@/hooks/useAccess';
import {
  useCashFlowQuery,
  usePositionQuery,
  useProfitAndLossQuery,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney, isoDate } from '@/lib/format';
import {
  Alert, Card, CardBody, CardHeader, ErrorState, Input, LoadingState, PageHeader,
  Select, Table, Td, Th,
} from '@/components/ui';
import { ExportMenu } from '@/components/ExportMenu';
import { DATE_PRESETS } from '@/components/DateRangeFilter';

/** How each figure reads: a cost is money out, and shows as one. */
type Tone = 'plain' | 'negative' | 'total';

/**
 * What the business earned, what moved, and what is left.
 *
 * Three statements on one screen because each one invites the question the
 * next answers: profit says what was earned, the cash statement says what
 * actually moved, and the position says what is left over. Read alone, the
 * first two are routinely mistaken for each other.
 */
export function FinancialReportsPage() {
  const format = useFormat();

  const [preset, setPreset] = useState('thisMonth');
  const [from, setFrom] = useState(isoDate(DATE_PRESETS.thisMonth.from()));
  const [to, setTo] = useState(isoDate(DATE_PRESETS.thisMonth.to()));

  const profit = useProfitAndLossQuery({ from, to });
  const cash = useCashFlowQuery({ from, to });
  const position = usePositionQuery({ asAt: to });

  const applyPreset = (key: string): void => {
    setPreset(key);
    const range = DATE_PRESETS[key];
    if (!range) return;
    setFrom(isoDate(range.from()));
    setTo(isoDate(range.to()));
  };

  /**
   * Money as an accountant reads it: a negative figure is bracketed, not
   * signed. A minus sign next to a currency symbol is easy to miss and easier
   * to mistake for a hyphen.
   */
  const money = (value: string | undefined): string => {
    const amount = Number(value ?? '0');
    const text = formatMoney(String(Math.abs(amount)), format);
    return amount < 0 ? `(${text})` : text;
  };

  if (profit.isError) {
    return (
      <>
        <PageHeader title="Financial reports" />
        <ErrorState message={apiErrorMessage(profit.error)} onRetry={() => void profit.refetch()} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Financial reports"
        description="What the business earned, what moved through its accounts, and what it is worth."
        action={
          <ExportMenu
            period={`${formatDate(from, format)} to ${formatDate(to, format)}`}
            items={[
              {
                label: 'Financial statements — profit, cash and position',
                path: '/reports/financial/export',
                params: { from, to },
              },
              {
                // The month the period ends in: "this month" and "last month" land
                // on the month they name.
                label: `End-of-month report — ${new Date(`${to.slice(0, 7)}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`,
                path: '/reports/monthly/export',
                params: { month: to.slice(0, 7) },
              },
            ]}
          />
        }
      />

      <Card className="mb-6">
        <div className="flex flex-wrap items-end gap-3 p-4">
          <div className="w-full sm:w-44">
            <Select label="Period" value={preset} onChange={(event) => applyPreset(event.target.value)}>
              {Object.entries(DATE_PRESETS).map(([key, range]) => (
                <option key={key} value={key}>{range.label}</option>
              ))}
              <option value="custom">Custom</option>
            </Select>
          </div>
          <div className="w-full sm:w-44">
            <Input
              label="From"
              type="date"
              value={from}
              max={to}
              onChange={(event) => {
                setFrom(event.target.value);
                setPreset('custom');
              }}
            />
          </div>
          <div className="w-full sm:w-44">
            <Input
              label="To"
              type="date"
              value={to}
              min={from}
              onChange={(event) => {
                setTo(event.target.value);
                setPreset('custom');
              }}
            />
          </div>
        </div>
      </Card>

      {profit.isLoading ? (
        <LoadingState label="Working out the figures…" />
      ) : profit.data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="lg:col-span-2">
            <CardBody>
              <div className="grid gap-4 sm:grid-cols-3">
                <Headline
                  icon={TrendingUp}
                  label="Net profit"
                  value={money(profit.data.netProfit)}
                  hint={
                    profit.data.netMarginPercent === null
                      ? 'No revenue to measure against'
                      : `${profit.data.netMarginPercent}% of revenue`
                  }
                  negative={Number(profit.data.netProfit) < 0}
                />
                <Headline
                  icon={Banknote}
                  label="Cash at the end"
                  value={money(cash.data?.summary.closing)}
                  hint={`${money(cash.data?.summary.movement)} moved in the period`}
                />
                <Headline
                  icon={Scale}
                  label="Net worth"
                  value={money(position.data?.netWorth)}
                  hint="Held less owed, derived"
                  negative={Number(position.data?.netWorth ?? '0') < 0}
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Profit and loss"
              description="A sale counts when it is made and an expense when it is approved, whether or not the money has moved."
            />
            <Table className="min-w-0">
              <tbody>
                <Line label="Revenue — goods" value={money(profit.data.revenue.goods)} />
                <Line label="Revenue — property" value={money(profit.data.revenue.property)} />
                <Line label="Revenue" value={money(profit.data.revenue.total)} tone="total" />
                <Line label="Cost of goods sold" value={money(profit.data.costOfSales.goods)} tone="negative" />
                <Line label="Cost of property sold" value={money(profit.data.costOfSales.property)} tone="negative" />
                <Line label="Gross profit" value={money(profit.data.grossProfit)} tone="total" />
                <Line
                  label="Operating expenses"
                  value={money(profit.data.operatingExpenses.total)}
                  tone="negative"
                />
                <Line label="Net profit" value={money(profit.data.netProfit)} tone="total" />
              </tbody>
            </Table>
            <CardBody>
              <p className="text-xs text-muted">
                Tax charged of {money(profit.data.revenue.taxCharged)} is collected for the
                authority and is not income. Discounts given:{' '}
                {money(profit.data.revenue.discountsGiven)}.
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Where it went"
              description="Operating expenses by category, largest first."
            />
            {profit.data.operatingExpenses.byCategory.length === 0 ? (
              <CardBody>
                <p className="text-sm text-muted">No expenses were approved in this period.</p>
              </CardBody>
            ) : (
              <Table className="min-w-0">
                <thead>
                  <tr>
                    <Th>Category</Th>
                    <Th numeric>Count</Th>
                    <Th numeric>Amount</Th>
                  </tr>
                </thead>
                <tbody>
                  {profit.data.operatingExpenses.byCategory.map((row) => (
                    <tr key={row.categoryId}>
                      <Td>{row.name}</Td>
                      <Td numeric className="text-secondary">{row.count}</Td>
                      <Td numeric className="font-medium">{money(row.amount)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Cash statement"
              description="What actually moved, read from the cash ledger. It will not equal the profit above, and is not meant to."
            />
            {cash.isLoading ? (
              <CardBody><LoadingState /></CardBody>
            ) : cash.data ? (
              <Table className="min-w-0">
                <tbody>
                  <Line label="Opening balance" value={money(cash.data.summary.opening)} />
                  <Line label="Received" value={money(cash.data.summary.received)} />
                  <Line label="Paid out" value={money(cash.data.summary.paid)} tone="negative" />
                  <Line label="Closing balance" value={money(cash.data.summary.closing)} tone="total" />
                  <Line
                    label="Of which from trading"
                    value={money(cash.data.summary.fromTrading)}
                  />
                </tbody>
              </Table>
            ) : null}
          </Card>

          <Card>
            <CardHeader title="By account" description="Every account, and what moved through it." />
            {cash.data && cash.data.byAccount.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <Th>Account</Th>
                    <Th numeric>Opening</Th>
                    <Th numeric>In</Th>
                    <Th numeric>Out</Th>
                    <Th numeric>Closing</Th>
                  </tr>
                </thead>
                <tbody>
                  {cash.data.byAccount.map((row) => (
                    <tr key={row.accountId}>
                      <Td>
                        <span className="font-medium">{row.name}</span>
                        {!row.isActive && <p className="text-xs text-muted">closed</p>}
                      </Td>
                      <Td numeric className="text-secondary">{money(row.opening)}</Td>
                      <Td numeric className="text-secondary">{money(row.received)}</Td>
                      <Td numeric className="text-secondary">{money(row.paid)}</Td>
                      <Td numeric className="font-medium">{money(row.closing)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <CardBody><p className="text-sm text-muted">No accounts have been opened.</p></CardBody>
            )}
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader
              title={`Position as at ${formatDate(to, format)}`}
              description="What the business holds and what it owes."
            />
            {position.data && (
              <Table className="min-w-0">
                <tbody>
                  <Line label="Cash in accounts" value={money(position.data.held.cash)} />
                  <Line label="Stock at cost" value={money(position.data.held.stockAtCost)} />
                  <Line label="Owed by customers" value={money(position.data.held.owedByCustomers)} />
                  <Line label="Land held, at cost" value={money(position.data.held.propertyAtCost)} />
                  <Line label="Total held" value={money(position.data.held.total)} tone="total" />
                  <Line label="Owed to suppliers" value={money(position.data.owed.toSuppliers)} tone="negative" />
                  <Line
                    label="Approved expenses not yet paid"
                    value={money(position.data.owed.approvedExpenses)}
                    tone="negative"
                  />
                  <Line label="Net worth" value={money(position.data.netWorth)} tone="total" />
                </tbody>
              </Table>
            )}
            <CardBody>
              <Alert tone="info">
                This is a statement of position, not a balance sheet. It is derived from the
                operational records — cash accounts, the stock ledger, unpaid sales and supplier
                invoices — and this system keeps no general ledger, so there is no double entry
                behind these figures. Stock and land are at cost, not at what they would sell for.
              </Alert>
            </CardBody>
          </Card>
        </div>
      ) : null}
    </>
  );
}

function Headline({
  icon: Icon,
  label,
  value,
  hint,
  negative,
}: {
  icon: typeof TrendingUp;
  label: string;
  value: string;
  hint: string;
  negative?: boolean;
}) {
  return (
    <div className="rounded-xl border border-subtle p-4">
      <p className="flex items-center gap-2 text-sm text-muted">
        <Icon className="size-4" aria-hidden />
        {label}
      </p>
      <p
        className={`mt-1 text-2xl font-semibold tabular ${
          negative ? 'text-red-600 dark:text-red-400' : ''
        }`}
      >
        {value}
      </p>
      <p className="mt-1 text-xs text-muted">{hint}</p>
    </div>
  );
}

/** One line of a statement. A total is ruled off; a cost reads as money out. */
function Line({ label, value, tone = 'plain' }: { label: string; value: string; tone?: Tone }) {
  return (
    <tr className={tone === 'total' ? 'border-t border-subtle' : undefined}>
      <Td className={tone === 'total' ? 'font-medium' : 'text-secondary'}>{label}</Td>
      <Td numeric className={tone === 'total' ? 'font-semibold' : undefined}>
        {tone === 'negative' && !value.startsWith('(') ? `(${value})` : value}
      </Td>
    </tr>
  );
}
